import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { MemberLayout } from "../components/MemberLayout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AuditForm } from "@/components/planning/AuditForm"

// Standalone Integrated Life Audit retake. Always starts from a blank form -- the
// member's previous wheel is not pre-filled because the point of retaking
// is a fresh, present-tense rating. Submitting POSTs to /app/member/wheel_snapshots
// which writes a new WheelSnapshot row and redirects back to the dashboard.
//
// The active QuarterlyCheckIn audit_scores are intentionally untouched.
export default function Retake({ currentUser, latestSnapshot }) {
  const [scores, setScores] = useState({})
  const [answered, setAnswered] = useState(0)
  const [complete, setComplete] = useState(false)
  const [processing, setProcessing] = useState(false)

  const submit = () => {
    if (!complete || processing) return
    setProcessing(true)
    router.post(
      "/app/member/wheel_snapshots",
      { audit_scores: scores },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
      },
    )
  }

  const lastTaken = latestSnapshot?.takenAt
    ? new Date(latestSnapshot.takenAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null

  return (
    <>
      <Head title="Complete the Integrated Life Audit Again" />
      <MemberLayout currentUser={currentUser}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Link
            href="/app/member"
            className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>

          <h1 className="font-display text-3xl mb-2">Complete the Integrated Life Audit Again</h1>
          <p className="text-zinc-500 mb-6">
            Answer each statement based on where you are right now. This saves a fresh
            snapshot of your wheel — your current quarter's rocks, KPIs, and goals
            stay untouched.
            {lastTaken && (
              <>
                {" "}
                <span className="text-zinc-400">Last taken {lastTaken}.</span>
              </>
            )}
          </p>

          <Card>
            <CardHeader>
              <CardTitle>
                {answered}/{AuditForm.TOTAL_QUESTIONS} answered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AuditForm
                onChange={({ scores: next, answered: a, complete: c }) => {
                  setScores(next)
                  setAnswered(a)
                  setComplete(c)
                }}
              />
              <div className="mt-6 flex justify-end gap-2">
                <Link href="/app/member">
                  <Button type="button" variant="kf-outline">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="button"
                  variant="gradient"
                  disabled={!complete || processing}
                  onClick={submit}
                >
                  {processing ? "Saving…" : "Save snapshot"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MemberLayout>
    </>
  )
}
